import { useCallback, useMemo } from 'react';
import { pickBy, trim } from 'lodash';
import { ButtonBase, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import SearchIcon from '@mui/icons-material/Search';
import { useFormValues, useSetValues } from '@client/hooks/formik.hooks';
import { Prompt } from '@client/components/globals/Modal/Prompt';
import { ProfileTypes } from '@sda/enums';
import { Clamp } from '@client/components/common/Clamp';
import { getLocation } from '@sda/utils/location';
import { getDateRange } from '@sda/utils/date';
import { useSelector } from '@client/hooks/redux.hooks';
import { getMapObject } from '@client/selectors/features/map';
import { FilterModal } from './FilterModal';
import type { FilterFormValues } from '../interfaces';
import type { Location } from '@sda/utils/location/interfaces';

const useStyles = makeStyles({ name: 'FilterBar' })((theme) => ({
  root: {
    backgroundColor: theme.palette.grey[200],
    borderRadius: theme.radii.large,
    borderWidth: 0,
    height: theme.spacing(4),
    padding: theme.spacing(0, 1 / 2),

    [theme.breakpoints.up('md')]: {
      minWidth: 320,
    },

    '& > div': {
      padding: theme.spacing(1, 1.5),

      '& > p': {
        userSelect: 'none',
      },
    },

    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  searchType: {
    borderRightColor: theme.palette.grey[300],
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    flex: 0,
  },
  location: {
    borderRightColor: theme.palette.grey[300],
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    flex: '0 1 auto',

    [theme.breakpoints.down('md')]: {
      '& > p': {
        wordBreak: 'break-word',
      },
    },
  },
  dates: {
    flex: '0 1 auto',
    minWidth: theme.spacing(10),
  },
  icon: {
    marginRight: theme.spacing(1 / 2),
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.grey[50],
    height: 24,
    width: 24,
    minWidth: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '& > svg': {
      fontSize: '18px',
    },
  },
}));

const whitelistedFields = [
  'searchType',
  'query',
  'googlePlaceId',
  'compiledAddress',
  'latLng',
  'dateRange',
  'filter',
  'venueType',
  'hasPA',
  'primaryGenre',
  'requiresPA',
];

const filterFields = (fields: any) => pickBy(fields, (_, key) => whitelistedFields.includes(key));

export const FilterBar = () => {
  const { classes } = useStyles();
  const map = useSelector(getMapObject);

  const setValues = useSetValues();
  const values = useFormValues<FilterFormValues>();

  const handleMapSynchronize = useCallback((result: Location) => {
    if (map && result) {
      map.panTo(getLocation(result));
    }
  }, [map]);

  const handleFilterButtonClick = useCallback(() => {
    Prompt.show({
      disableCloseButton: true,
      buttonLabel: 'Search',
      buttonSize: 'medium',
      title: null,
      initialValues: filterFields(values),
      maxWidth: 'wideModal',
      buttons: [],
      component: (
        <FilterModal />
      ),
      onSubmit: (updatedValues) => {
        void setValues({
          ...values,
          ...updatedValues,
        }, false);

        handleMapSynchronize(updatedValues.latLng);

        Prompt.hide();
      },
    });
  }, [values, setValues, handleMapSynchronize]);

  const location = useMemo(() => {
    const compiledAddress = values.compiledAddress || '';
    const parts = compiledAddress.split(',');

    return parts && trim(parts[0]);
  }, [values.compiledAddress]);

  const [startDate, endDate] = values.dateRange;

  return (
    <ButtonBase
      className={classes.root}
      onClick={handleFilterButtonClick}
    >
      <div className={classes.searchType}>
        <Typography>
          {values.searchType === ProfileTypes.ACT ? 'Artists' : 'Hosts'}
        </Typography>
      </div>

      <div className={classes.location}>
        <Clamp lines={1}>
          {location || 'Map area'}
        </Clamp>
      </div>

      <div className={classes.dates}>
        <Clamp lines={1}>
          {startDate ? getDateRange({ startDate, endDate }) : 'Any time'}
        </Clamp>
      </div>

      <p className={classes.icon}>
        <SearchIcon />
      </p>
    </ButtonBase>
  );
};
